import { IterableConfig, type IterableAction } from '@iterable/web-sdk'
import { captureMessage } from '@sentry/react'
import { isPathAllowed } from 'auth/roles'
import { destinationOf } from './types'

/**
 * Where an announcement's buttons are allowed to send a provider.
 *
 * Both of Iterable's action types land here. The whole point of the feature is that Marketing
 * publishes without a release, which means these handlers are reached by strings *we did not
 * write*. So every path is closed by default: an unrecognised custom action and a disallowed
 * route both no-op, loudly in Sentry but silently on screen, leaving the announcement itself
 * rendered. A typo in a campaign must never be able to break a clinician's dashboard.
 */

type Navigate = (path: string) => void

let navigate: Navigate | undefined
let currentRoles: string[] = []

/**
 * Hands the router and the signed-in provider's roles to the SDK's static handlers.
 *
 * `IterableConfig.urlHandler` / `customActionHandler` are static fields the SDK calls from outside
 * React, so they cannot read hooks. This is the same module-setter shape `initOrvalTokenModules`
 * uses to give the shared fetcher access to the Redux store.
 */
export function setIterableNavigationContext(context: { navigate: Navigate; roles: string[] }): void {
  navigate = context.navigate
  currentRoles = context.roles
}

/**
 * Custom actions, keyed by the `<id>` in `action://<id>`.
 *
 * Deliberately near-empty: the ticket lists button requirements as still pending, so rather than
 * guess at ids, this ships as a registry with a safe fallback. Adding one later is a single entry.
 * `dismiss` is **not** here — dismissal has to remove the message from the list, which only the
 * placement can do, so it is intercepted before an action ever reaches the SDK.
 */
const customActions: Record<string, (() => void) | undefined> = {}

/**
 * Routes an in-app destination, refusing anything this provider's roles cannot reach. A page marked
 * `newTab` opens in a tab of its own, past the same role check.
 *
 * `pathname` and `target` are separate arguments on purpose. `isPathAllowed` matches a bare route
 * path exactly against the role's allow-list, so handing it a real URL — which for this app almost
 * always carries a query, as in `/patientcharts/patient?id=123` — never matches and refuses a page
 * the role is in fact allowed to open. The router still gets the whole thing.
 */
function navigateInApp(pathname: string, target: string, newTab: boolean): void {
  // A pharmacist following an announcement deep-link to, say, /medications would otherwise be
  // bounced to their own home by the catch-all route, which reads as a broken announcement.
  if (!isPathAllowed(pathname, currentRoles)) {
    captureMessage(`Iterable announcement linked to a path this role cannot open: ${pathname}`)
    return
  }
  if (newTab) window.open(target, '_blank', 'noopener,noreferrer')
  else navigate?.(target)
}

/**
 * Installs the SDK's global click handlers. Idempotent, and safe to call before a user is known —
 * `IterableConfig` holds plain static fields, not SDK state.
 */
export function installIterableActionHandlers(): void {
  IterableConfig.urlHandler = {
    // Always returns true, refusals included. `false` does not mean "do nothing" to the SDK: its
    // `openUri` falls back to `window.open(uri, '_blank')` itself — no `noopener`, and none of the
    // checks below — so every refusal would open the very URL it refused.
    handleIterableURL(uri: string): boolean {
      const destination = destinationOf(uri)
      switch (destination.kind) {
        case 'in-app':
          // Through the router: a full page load would drop the provider's in-memory state and,
          // during a visit, their Daily call.
          navigateInApp(destination.pathname, destination.path, destination.newTab)
          break
        case 'external':
          // `noopener` matters more than usual here: the destination is authored outside the codebase.
          window.open(destination.href, '_blank', 'noopener,noreferrer')
          break
        case 'custom':
          // The SDK routes these prefixes to the custom-action handler first, so one only lands here
          // if it was passed in directly. It is not a URL either way.
          captureMessage(`Iterable announcement sent a custom action to the URL handler: ${uri}`)
          break
        case 'refused':
          captureMessage(`Iterable announcement had a URL it will not open (${destination.reason}): ${uri}`)
          break
      }
      return true
    },
  }

  IterableConfig.customActionHandler = {
    handleIterableCustomAction(action: IterableAction): boolean {
      const id = action.type.replace(/^(action|itbl):\/\//, '')
      const handler = customActions[id]
      if (!handler) {
        captureMessage(`Iterable announcement used an unknown custom action: ${action.type}`)
        return false
      }
      handler()
      return true
    },
  }
}

/** Test seam: clears the module state the static handlers close over. */
export function resetIterableActionsForTests(): void {
  navigate = undefined
  currentRoles = []
  IterableConfig.urlHandler = null
  IterableConfig.customActionHandler = null
}
